import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView, useAnimation } from 'framer-motion'
import { FolderOpen, User } from 'lucide-react'
import { FaAndroid, FaWindows } from 'react-icons/fa'
import Hero from '../components/Hero'
import RockTerminal from '../components/RockTerminal'

const bgParticles = [
  { id: 'p1', x: '15%', y: '25%', color: '#00d9ff', delay: 0, duration: 16, dx: 8, dy: -10, size: '2px' },
  { id: 'p2', x: '75%', y: '20%', color: '#a855f7', delay: 2, duration: 18, dx: -6, dy: -12, size: '2px' },
  { id: 'p3', x: '30%', y: '65%', color: '#00d9ff', delay: 4, duration: 15, dx: 10, dy: -8, size: '2px' },
  { id: 'p4', x: '85%', y: '70%', color: '#a855f7', delay: 1, duration: 20, dx: -8, dy: -14, size: '2px' },
  { id: 'p5', x: '55%', y: '40%', color: '#00d9ff', delay: 3, duration: 17, dx: 6, dy: -10, size: '4px' },
  { id: 'p6', x: '10%', y: '35%', color: '#00d9ff', delay: 0, duration: 10, dx: 12, dy: -15, size: '4px' },
  { id: 'p7', x: '80%', y: '15%', color: '#a855f7', delay: 1, duration: 12, dx: -8, dy: -20, size: '4px' },
  { id: 'p8', x: '5%', y: '15%', color: '#00d9ff', delay: 0.5, duration: 7, dx: 15, dy: -12, size: '6px' },
  { id: 'p9', x: '90%', y: '40%', color: '#a855f7', delay: 2, duration: 8, dx: -12, dy: -15, size: '6px' },
]
function RockButton({ isOpen, onToggle }) {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <>
      <motion.button
        onClick={onToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        animate={{
          boxShadow: isOpen
            ? ['0 0 20px rgba(255,0,110,0.5)', '0 0 40px rgba(255,0,110,0.3)', '0 0 20px rgba(255,0,110,0.5)']
            : ['0 0 20px rgba(0,217,255,0.4)', '0 0 40px rgba(0,217,255,0.2)', '0 0 20px rgba(0,217,255,0.4)']
        }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        style={{
          position: 'fixed',
          bottom: '1.75rem',
          right: '1.75rem',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.6rem 1.1rem 0.6rem 0.8rem',
          borderRadius: '50px',
          border: `1px solid ${isOpen ? 'rgba(255,0,110,0.6)' : 'rgba(0,217,255,0.5)'}`,
          background: isOpen ? 'rgba(255,0,110,0.1)' : 'rgba(0,217,255,0.08)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          cursor: 'pointer',
          outline: 'none',
          transition: 'border 0.3s ease, background 0.3s ease'
        }}
      >
        <motion.div
          animate={{
            scale: [1, 1.5, 1],
            opacity: [1, 0.4, 1]
          }}
          transition={{ duration: 1.5, repeat: Infinity }}
          style={{
            width: '7px',
            height: '7px',
            borderRadius: '50%',
            background: isOpen ? '#ff006e' : '#00d9ff',
            boxShadow: isOpen ? '0 0 8px #ff006e' : '0 0 8px #00d9ff',
            flexShrink: 0,
            transition: 'background 0.3s ease, box-shadow 0.3s ease'
          }}
        />
        <span style={{
          fontSize: '0.72rem',
          fontWeight: 700,
          color: isOpen ? '#ff006e' : '#00d9ff',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          fontFamily: 'monospace',
          whiteSpace: 'nowrap',
          transition: 'color 0.3s ease'
        }}>
          {isOpen ? 'CLOSE' : 'ROCK // AI'}
        </span>
        <span style={{ fontSize: '0.95rem', lineHeight: 1 }}>
          {isOpen ? '✕' : ''}
        </span>
      </motion.button>
      {isHovered && !isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            bottom: '5.5rem',
            right: '1.75rem',
            zIndex: 9999,
            background: 'rgba(5,5,8,0.95)',
            border: '1px solid rgba(0,217,255,0.2)',
            borderRadius: '8px',
            padding: '7px 13px',
            fontSize: '0.68rem',
            color: 'rgba(255,255,255,0.6)',
            whiteSpace: 'nowrap',
            fontFamily: 'monospace',
            letterSpacing: '0.05em',
            pointerEvents: 'none'
          }}
        >
          💬 Ask Rock anything about Rohan
          <div style={{
            position: 'absolute',
            top: '100%',
            right: '1.8rem',
            width: 0,
            height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '5px solid rgba(0,217,255,0.2)'
          }} />
        </motion.div>
      )}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            position: 'fixed',
            bottom: '5.5rem',
            right: '1.75rem',
            zIndex: 9999,
          }}
        >
          <RockTerminal onClose={onToggle} />
        </motion.div>
      )}
    </>
  )
}

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
}

const scaleIn = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: 'easeOut' } },
}

const cardHeading = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
}

function MarqueeBar() {
  const items = [
    'Flutter', 'Dart', 'React', 'Python', 'C Language',
    'Nostalgia Cam', 'AI Tools', 'Game Dev', 'Android', 'Windows'
  ]
  const repeated = [...items, ...items, ...items]

  return (
    <div style={{
      overflow: 'hidden',
      borderTop: '1px solid rgba(255,255,255,0.06)',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      padding: '1rem 0',
      background: 'transparent',
      margin: '0'
    }}>
      <motion.div
        animate={{ x: ['0%', '-33.33%'] }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: 'linear'
        }}
        style={{ display: 'flex', gap: '0', whiteSpace: 'nowrap' }}
      >
        {repeated.map((item, i) => (
          <motion.span
            key={i}
            whileHover={{ color: '#00d9ff', scale: 1.05 }}
            transition={{ duration: 0.15 }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '0 1.5rem',
              fontSize: '0.65rem',
              fontWeight: 500,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              fontFamily: 'monospace',
              color: 'rgba(255,255,255,0.2)',
              cursor: 'default',
            }}
          >
            {item}
            <span style={{ color: '#00d9ff', fontSize: '0.5rem' }}>●</span>
          </motion.span>
        ))}
      </motion.div>
    </div>
  )
}

export default function Home() {
  const featuredRef = useRef(null)
  const canvasRef = useRef(null)

  const [rockOpen, setRockOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  const isFeaturedInView = useInView(featuredRef, { once: false, amount: 0.05 })
  const featuredControls = useAnimation()

  useEffect(() => {
    if (isFeaturedInView) {
      featuredControls.start('visible')
    } else {
      featuredControls.start('hidden')
    }
  }, [isFeaturedInView, featuredControls])

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    let animationId
    let particles = []

    const resize = () => {
      const parent = canvas.parentElement
      canvas.width = parent.offsetWidth
      canvas.height = parent.offsetHeight
    }
    resize()
    window.addEventListener('resize', resize)

    for (let i = 0; i < 80; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.8,
        vy: (Math.random() - 0.5) * 0.8,
        r: Math.random() * 1.5 + 1,
        opacity: Math.random() * 0.17 + 0.08,
      })
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      for (const p of particles) {
        p.x += p.vx
        p.y += p.vy
        if (p.x < 0) p.x = canvas.width
        if (p.x > canvas.width) p.x = 0
        if (p.y < 0) p.y = canvas.height
        if (p.y > canvas.height) p.y = 0
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(255, 255, 255, ${p.opacity})`
        ctx.fill()
      }
      animationId = requestAnimationFrame(animate)
    }
    animate()

    return () => {
      window.removeEventListener('resize', resize)
      cancelAnimationFrame(animationId)
    }
  }, [])

  return (
    <div style={{ position: 'relative', overflow: 'hidden', background: '#050508' }}>
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '80vw',
        height: '60vh',
        background: 'radial-gradient(ellipse, rgba(0,217,255,0.07) 0%, rgba(208,0,255,0.04) 40%, transparent 70%)',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      {bgParticles.map((p) => (
        <motion.div
          key={p.id}
          style={{
            position: 'fixed',
            left: p.x,
            top: p.y,
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: p.color,
            boxShadow: `0 0 6px ${p.color}`,
            opacity: 0.2,
            zIndex: 0,
            pointerEvents: 'none',
          }}
          animate={{ x: [0, p.dx, 0], y: [0, p.dy, 0], opacity: [0.1, 0.25, 0.1] }}
          transition={{ duration: p.duration, repeat: Infinity, ease: 'easeInOut', delay: p.delay }}
        />
      ))}

      <div style={{ position: 'relative', zIndex: 1 }}>
        <Hero />
        <MarqueeBar />
        <RockButton isOpen={rockOpen} onToggle={() => setRockOpen(o => !o)} />

        <section ref={featuredRef} style={{
          padding: isMobile ? '3rem 1rem' : '6rem 2rem',
          background: 'transparent',
          borderTop: '1px solid rgba(255,255,255,0.04)',
          borderBottom: '1px solid rgba(255,255,255,0.04)'
        }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

            <motion.h2
              initial="hidden"
              animate={featuredControls}
              variants={cardHeading}
              style={{
                fontSize: isMobile ? 'clamp(2rem, 12vw, 3rem)' : 'clamp(2.5rem, 8vw, 6rem)',
                fontWeight: 900,
                textTransform: 'uppercase',
                letterSpacing: '-0.02em',
                background: 'linear-gradient(180deg, #ffffff 0%, rgba(255,255,255,0.75) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: isMobile ? '1.5rem' : '3rem',
                lineHeight: 1
              }}
            >Featured Work</motion.h2>

          <motion.div
            initial="hidden"
            animate={featuredControls}
            variants={stagger}
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '1.5rem',
              alignItems: 'stretch'
            }}
          >

            <motion.div
              variants={scaleIn}
              whileHover={{ y: -8, borderColor: 'rgba(0,217,255,0.5)', boxShadow: '0 20px 40px rgba(0,217,255,0.12)' }}
              transition={{ duration: 0.25 }}
              style={{
                background: '#0d1117',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: isMobile ? 'row' : 'column',
                height: isMobile ? '100px' : 'auto',
                cursor: 'pointer'
              }}
            >
              <div style={{
                width: isMobile ? '100px' : '100%',
                height: isMobile ? '100px' : '180px',
                flexShrink: isMobile ? 0 : 'auto',
                background: 'linear-gradient(135deg, rgba(0,217,255,0.06) 0%, rgba(0,217,255,0.02) 100%)',
                borderBottom: isMobile ? 'none' : '1px solid rgba(255,255,255,0.06)',
                borderRight: isMobile ? '1px solid rgba(255,255,255,0.06)' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <motion.div
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.08, 0.18, 0.08],
                  }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                  style={{
                    position: 'absolute',
                    width: '120px', height: '120px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(0,217,255,0.4), transparent)',
                    pointerEvents: 'none'
                  }}
                />
                <FolderOpen size={isMobile ? 40 : 64} color="#00d9ff" strokeWidth={1.2} />
              </div>
              <div style={{ padding: isMobile ? '1rem' : '1.5rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem', justifyContent: isMobile ? 'center' : 'flex-start' }}>
                <h3 style={{ color: 'white', fontWeight: 700, fontSize: isMobile ? '0.9rem' : '1rem', margin: 0 }}>My Projects</h3>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: isMobile ? '0.75rem' : '0.82rem', margin: 0, lineHeight: 1.6 }}>{isMobile ? 'Python, C, Flutter, and more.' : 'Python, C, Flutter, and more.'}</p>
                <Link to="/projects" style={{ marginTop: 'auto', paddingTop: isMobile ? '0.5rem' : '1rem', color: '#00d9ff', fontSize: isMobile ? '0.75rem' : '0.8rem', fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  View
                  <motion.span
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                  >→</motion.span>
                </Link>
              </div>
            </motion.div>



            <motion.div
              variants={scaleIn}
              whileHover={{ y: -8, borderColor: 'rgba(0,217,255,0.5)', boxShadow: '0 20px 40px rgba(0,217,255,0.12)' }}
              transition={{ duration: 0.25 }}
              style={{
                background: '#0d1117',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: isMobile ? 'row' : 'column',
                height: isMobile ? '100px' : 'auto',
                cursor: 'pointer'
              }}
            >
              <div style={{
                width: isMobile ? '100px' : '100%',
                height: isMobile ? '100px' : '180px',
                flexShrink: isMobile ? 0 : 'auto',
                background: 'linear-gradient(135deg, rgba(0,217,255,0.06) 0%, rgba(0,217,255,0.02) 100%)',
                borderBottom: isMobile ? 'none' : '1px solid rgba(255,255,255,0.06)',
                borderRight: isMobile ? '1px solid rgba(255,255,255,0.06)' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <motion.div
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.08, 0.18, 0.08],
                  }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
                  style={{
                    position: 'absolute',
                    width: '120px', height: '120px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(0,217,255,0.4), transparent)',
                    pointerEvents: 'none'
                  }}
                />
                <User size={isMobile ? 40 : 64} color="#00d9ff" strokeWidth={1.2} />
              </div>
              <div style={{ padding: isMobile ? '1rem' : '1.5rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem', justifyContent: isMobile ? 'center' : 'flex-start' }}>
                <h3 style={{ color: 'white', fontWeight: 700, fontSize: isMobile ? '0.9rem' : '1rem', margin: 0 }}>About Me</h3>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: isMobile ? '0.75rem' : '0.82rem', margin: 0, lineHeight: 1.6 }}>My story, skills, and journey.</p>
                <Link to="/about" style={{ marginTop: 'auto', paddingTop: isMobile ? '0.5rem' : '1rem', color: '#00d9ff', fontSize: isMobile ? '0.75rem' : '0.8rem', fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  View
                  <motion.span
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                  >→</motion.span>
                </Link>
              </div>
            </motion.div>

          </motion.div>

            <motion.div
              initial="hidden"
              animate={featuredControls}
              variants={scaleIn}
              whileHover={{ y: -4, borderColor: 'rgba(0,217,255,0.4)', boxShadow: '0 16px 40px rgba(0,217,255,0.1)' }}
              transition={{ duration: 0.25 }}
              style={{
                background: '#0d1117',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'stretch',
                marginTop: '2rem',
                cursor: 'pointer'
              }}
            >
              <div style={{
                width: isMobile ? '100%' : '320px',
                height: isMobile ? '200px' : 'auto',
                flexShrink: 0,
                background: 'linear-gradient(135deg, rgba(0,217,255,0.06) 0%, rgba(0,217,255,0.02) 100%)',
                borderBottom: isMobile ? '1px solid rgba(255,255,255,0.06)' : 'none',
                borderRight: isMobile ? 'none' : '1px solid rgba(255,255,255,0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}>
                <img
                  src='/images/projects/nostalgia-cam.gif'
                  alt="Nostalgia Cam"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div style={{
                padding: isMobile ? '1.5rem' : '2rem',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    fontFamily: 'monospace',
                    color: '#00d9ff',
                    border: '1px solid rgba(0,217,255,0.3)',
                    borderRadius: '20px',
                    padding: '2px 10px',
                  }}>Side Project</span>
                </div>
                <h3 style={{ color: 'white', fontWeight: 700, fontSize: '1.5rem', margin: 0 }}>Nostalgia Cam</h3>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>
                  A nostalgic sketch tool — scribble your memories with a hand-drawn, retro camera aesthetic.
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                  {['HTML', 'CSS', 'JavaScript'].map(tag => (
                    <span key={tag} style={{
                      padding: '3px 10px',
                      borderRadius: '20px',
                      border: '1px solid rgba(0,217,255,0.25)',
                      color: 'rgba(0,217,255,0.8)',
                      fontSize: '0.7rem',
                    }}>{tag}</span>
                  ))}
                </div>
                <a
                  href="https://nostalgia-cam.pages.dev/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    marginTop: 'auto',
                    paddingTop: '0.5rem',
                    color: '#00d9ff',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                  }}
                >
                  Visit Live Site
                  <motion.span
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                  >→</motion.span>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              animate={featuredControls}
              variants={scaleIn}
              whileHover={{ y: -4, borderColor: 'rgba(0,217,255,0.4)', boxShadow: '0 16px 40px rgba(0,217,255,0.1)' }}
              transition={{ duration: 0.25 }}
              style={{
                background: '#0d1117',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'stretch',
                marginTop: '2rem',
                cursor: 'pointer'
              }}
            >
              <div style={{
                width: isMobile ? '100%' : '320px',
                height: isMobile ? '200px' : 'auto',
                flexShrink: 0,
                background: 'linear-gradient(135deg, rgba(0,217,255,0.06) 0%, rgba(0,217,255,0.02) 100%)',
                borderBottom: isMobile ? '1px solid rgba(255,255,255,0.06)' : 'none',
                borderRight: isMobile ? 'none' : '1px solid rgba(255,255,255,0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}>
                <img
                  src='/images/projects/TeleDrive.png'
                  alt="Tele Drive"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div style={{
                padding: isMobile ? '1.5rem' : '2rem',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    fontFamily: 'monospace',
                    color: '#00d9ff',
                    border: '1px solid rgba(0,217,255,0.3)',
                    borderRadius: '20px',
                    padding: '2px 10px',
                  }}>App</span>
                </div>
                <h3 style={{ color: 'white', fontWeight: 700, fontSize: '1.5rem', margin: 0 }}>Tele Drive</h3>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>
                  Unlimited cloud storage, powered by Telegram. A Flutter mobile app and Tauri desktop client that turn your Telegram account into a personal file server — no proxies, no third-party storage, no monthly fees.
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                  {['Dart', 'TypeScript', 'JavaScript', 'CSS', 'Shell'].map(tag => (
                    <span key={tag} style={{
                      padding: '3px 10px',
                      borderRadius: '20px',
                      border: '1px solid rgba(0,217,255,0.25)',
                      color: 'rgba(0,217,255,0.8)',
                      fontSize: '0.7rem',
                    }}>{tag}</span>
                  ))}
                </div>
                <div style={{ marginTop: 'auto', paddingTop: '0.5rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <motion.a
                    href="https://github.com/rohandalvi369/teledrive-/releases/download/v1.0.0/teledrive_.apk"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02, background: 'rgba(0,217,255,0.05)', borderColor: 'rgba(0,217,255,0.3)' }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '50px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <FaAndroid size={16} />
                    APK
                  </motion.a>
                  <motion.a
                    href="https://github.com/rohandalvi369/teledrive-/releases/download/v1.0.0/TeleDrive__x64-setup.exe"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02, background: 'rgba(0,217,255,0.05)', borderColor: 'rgba(0,217,255,0.3)' }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '50px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <FaWindows size={16} />
                    EXE
                  </motion.a>
                </div>
              </div>
            </motion.div>

        </div>
      </section>
      </div>
    </div>
  )
}
